'use client';
import { skillRank, skillRankCombined } from '@gravllift/halo-helpers';
import '@gravllift/utilities';
import { GameVariantCategory, MatchOutcome } from 'halo-infinite-api';
import { DateTime, Duration } from 'luxon';
import CsrDeltaDisplay from '../csr-delta-display/csr-delta-display';
import GameVariantCategoryDisplay from '../game-variant-category-display/game-variant-category-display';
import GamertagDisplay from '../match/gamertag-display';
import OutcomeDisplay from '../outcome-display/outcome-display';
import { counterfactualColumns } from './counterfactual-columns';
import { statsColumns } from './stats-columns';
import {
  TableCategory,
  TableColumn as _TableColumn,
  TableColumnWithFormat as _TableColumnWithFormat,
} from './types';

const teamStatsColumns = statsColumns('Team');
const playerStatsColumns = statsColumns('Player');

export const playerStatsCategory = {
  text: 'Player Stats',
  children: playerStatsColumns.categories,
};

type TableColumn = _TableColumn & {
  id: number;
};

type TableColumnWithFormat<T extends React.ReactNode> = {
  id: number;
} & _TableColumnWithFormat<T>;

export const columns = {
  'Match Id': {
    id: 0,
    text: 'Match Id',
    level: 'Match',
    rawValue: ({ match }) => match.MatchId,
    tooltip: 'Unique identifier generated by the game for this match',
  },
  'Start Time': {
    id: 1,
    text: 'Start Time',
    level: 'Match',
    rawValue: ({ match }) => match.MatchInfo.StartTime,
    queryValue: ({ match }) =>
      DateTime.fromISO(match.MatchInfo.StartTime).set({
        second: 0,
        millisecond: 0,
      }),
    format: (startTime) =>
      DateTime.fromISO(startTime).toLocaleString(DateTime.DATETIME_SHORT),
  } as TableColumnWithFormat<string>,
  'End Time': {
    id: 212,
    text: 'End Time',
    level: 'Match',
    rawValue: ({ match }) => match.MatchInfo.EndTime,
    queryValue: ({ match }) =>
      DateTime.fromISO(match.MatchInfo.EndTime).set({
        second: 0,
        millisecond: 0,
      }),
    format: (endTime) =>
      DateTime.fromISO(endTime).toLocaleString(DateTime.DATETIME_SHORT),
  } as TableColumnWithFormat<string>,
  Playlist: {
    id: 2,
    text: 'Playlist',
    level: 'Match',
    rawValue: ({ match }) =>
      match.MatchInfo.Playlist != null &&
      'PublicName' in match.MatchInfo.Playlist
        ? match.MatchInfo.Playlist.PublicName
        : match.MatchInfo.Playlist?.AssetId,
  },
  Map: {
    id: 3,
    text: 'Map',
    level: 'Match',
    rawValue: ({ match }) =>
      'PublicName' in match.MatchInfo.MapVariant
        ? match.MatchInfo.MapVariant.PublicName
        : match.MatchInfo.MapVariant.AssetId,
  },
  'Game Variant Category': {
    id: 4,
    text: 'Game Variant Category',
    level: 'Match',
    rawValue: ({ match }) => match.MatchInfo.GameVariantCategory,
    format: (gameVariantCategory) => (
      <GameVariantCategoryDisplay gameVariantCategory={gameVariantCategory} />
    ),
  } as TableColumnWithFormat<GameVariantCategory>,
  'Game Variant': {
    id: 5,
    text: 'Game Variant',
    level: 'Match',
    rawValue: ({ match }) =>
      'PublicName' in match.MatchInfo.UgcGameVariant
        ? match.MatchInfo.UgcGameVariant.PublicName
        : match.MatchInfo.UgcGameVariant.AssetId,
  },
  Duration: {
    id: 6,
    text: 'Duration',
    level: 'Match',
    cellProps: { isNumeric: true },
    rawValue: ({ match }) => match.MatchInfo.PlayableDuration,
    format: (duration) => Duration.fromISO(duration).toFormat('m:ss'),
  } as TableColumnWithFormat<string>,
  Outcome: {
    id: 7,
    text: 'Outcome',
    level: 'Team',
    rawValue: ({ team, player }) => player.Outcome ?? team?.Outcome,
    format: (outcome) => <OutcomeDisplay outcome={outcome} />,
  } as TableColumnWithFormat<MatchOutcome>,
  'Player Team MMR': {
    id: 8,
    text: 'Player Team',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) => 'Skill' in player && player.Skill?.TeamMmr,
    format: (teamMmr) =>
      typeof teamMmr === 'number' ? teamMmr.toFixed(2) : teamMmr,
    tooltip: "Player team's average matchmaking rank",
  } as TableColumnWithFormat<number | 'Infinity' | undefined>,
  'Enemy Team MMR': {
    id: 9,
    text: 'Enemy Team',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) =>
      'Skill' in player &&
      player.Skill &&
      Object.entries('Skill' in player && player.Skill.TeamMmrs).find(
        ([teamId]) => +teamId !== ('Skill' in player && player.Skill?.TeamId)
      )?.[1],
    format: (teamMmr) =>
      typeof teamMmr === 'number' ? teamMmr.toFixed(2) : teamMmr,
    tooltip: "Enemy team's average matchmaking rank",
  } as TableColumnWithFormat<number | 'Infinity' | undefined>,
  'Player Team CSR': {
    id: 10,
    text: 'Player Team',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team, player }) => {
      if (
        match.MatchInfo.Playlist &&
        'PublicName' in match.MatchInfo.Playlist &&
        'HasCsr' in match.MatchInfo.Playlist &&
        match.MatchInfo.Playlist.HasCsr === false
      ) {
        return null;
      }
      return 'Skill' in player && player.Skill
        ? team &&
            'Players' in team &&
            team.Players.filter(
              (p) =>
                'Skill' in p &&
                p.Skill &&
                p.Skill.RankRecap.PreMatchCsr.Value > -1
            )
              .map((p) =>
                'Skill' in p ? p.Skill?.RankRecap.PreMatchCsr.Value : null
              )
              .average()
        : null;
    },
    format: (csr) => csr?.toFixed(2),
    tooltip: "Player team's average competitive skill ranking",
  } as TableColumnWithFormat<number | null>,
  'Enemy Team CSR': {
    id: 11,
    text: 'Enemy Team',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team, player }) => {
      if (
        match.MatchInfo.Playlist &&
        'PublicName' in match.MatchInfo.Playlist &&
        'HasCsr' in match.MatchInfo.Playlist &&
        match.MatchInfo.Playlist.HasCsr === false
      ) {
        return null;
      }
      return 'Skill' in player && player.Skill && 'MatchStats' in match
        ? match.MatchStats.Players.filter(
            (p) =>
              p.LastTeamId !== team?.TeamId &&
              p.Skill &&
              p.Skill?.RankRecap.PreMatchCsr.Value > -1
          )
            .map((p) => p.Skill?.RankRecap.PreMatchCsr.Value)
            .average()
        : null;
    },
    format: (csr) => csr?.toFixed(2),
    tooltip: "Enemy team's average competitive skill ranking",
  } as TableColumnWithFormat<number | null>,
  'Player Team Score': {
    id: 12,
    text: 'Player Team',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) =>
      'MatchStats' in match &&
      match.MatchStats.Teams.find((t) => t.TeamId === team?.TeamId)?.Stats
        .CoreStats.Score,
  },
  'Enemy Team Score': {
    id: 13,
    text: 'Enemy Team',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) =>
      'MatchStats' in match &&
      match.MatchStats.Teams.find((t) => t.TeamId !== team?.TeamId)?.Stats
        .CoreStats.Score,
  },
  ...teamStatsColumns.columns,
  Season: {
    id: 83,
    text: 'Season',
    level: 'Match',
    rawValue: ({ match }) => match.MatchInfo.SeasonId,
  },
  'Team Members': {
    id: 84,
    text: 'Team Members',
    level: 'Team',
    rawValue: ({ team }) =>
      team &&
      'Players' in team &&
      team.Players.map((p) => ('gamertag' in p ? p.gamertag : p.xuid))
        .sortBy()
        .join(', '),
    format: (_, { team }) =>
      team &&
      'Players' in team &&
      team.Players.map((g) => (
        <GamertagDisplay player={g} key={g.PlayerId} fetchIfMissing />
      )),
  },
  'Enemy Team Members': {
    id: 85,
    text: 'Enemy Team Members',
    level: 'Team',
    rawValue: ({ match, team }) =>
      'MatchStats' in match &&
      match.MatchStats.Teams.filter((t) => t.TeamId != team?.TeamId)
        .flatMap((t) => t.Players.map((p) => p.gamertag || p.xuid).sortBy())
        .join(', '),
    format: (_, { match, team }) =>
      'MatchStats' in match &&
      match.MatchStats.Teams.filter((t) => t.TeamId != team?.TeamId).flatMap(
        (t) =>
          t.Players.map((g) => (
            <GamertagDisplay player={g} key={g.xuid} fetchIfMissing />
          ))
      ),
  },
  'Team Expected Skill Rank Average': {
    id: 86,
    text: 'Average',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      return (
        (team &&
          'Players' in team &&
          team.Players.map((p) =>
            'Skill' in p ? skillRankCombined(p.Skill, 'Expected') : null
          ).average()) ||
        null
      );
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Player team's average expected skill rank",
  } as TableColumnWithFormat<number | null>,
  'Team Expected Skill Rank Min': {
    id: 87,
    text: 'Min',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const truthyValues =
        team && 'Players' in team
          ? team.Players.map((p) =>
              'Skill' in p ? skillRankCombined(p.Skill, 'Expected') : null
            ).filter((v): v is number => v != null)
          : [];
      return truthyValues?.length ? Math.min(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Lowest expected skill rank among player team's members",
  } as TableColumnWithFormat<number | null>,
  'Team Expected Skill Rank Max': {
    id: 88,
    text: 'Max',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const truthyValues =
        team && 'Players' in team
          ? team.Players.map((p) =>
              'Skill' in p ? skillRankCombined(p.Skill, 'Expected') : null
            ).filter((v): v is number => v != null)
          : [];
      return truthyValues?.length ? Math.max(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Highest expected skill rank among player team's members",
  } as TableColumnWithFormat<number | null>,
  'Team Performance Skill Rank Kills Average': {
    id: 89,
    text: 'Average',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      return (
        (team &&
          'Players' in team &&
          team.Players.map((p) =>
            'Skill' in p ? skillRank(p.Skill, 'Kills', 'Count') : null
          ).average()) ||
        null
      );
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Player team's average performance skill rank - kills",
  } as TableColumnWithFormat<number | null>,
  'Team Performance Skill Rank Kills Min': {
    id: 90,
    text: 'Min',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const truthyValues =
        team && 'Players' in team
          ? team.Players.map((p) =>
              'Skill' in p ? skillRank(p.Skill, 'Kills', 'Count') : null
            ).filter((v): v is number => v != null)
          : [];
      return truthyValues?.length ? Math.min(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Lowest performance skill rank - kills among player team's members",
  } as TableColumnWithFormat<number | null>,
  'Team Performance Skill Rank Kills Max': {
    id: 91,
    text: 'Max',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const truthyValues =
        team && 'Players' in team
          ? team.Players.map((p) =>
              'Skill' in p ? skillRank(p.Skill, 'Kills', 'Count') : null
            ).filter((v): v is number => v != null)
          : [];
      return truthyValues?.length ? Math.max(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Highest performance skill rank - kills among player team's members",
  } as TableColumnWithFormat<number | null>,
  'Team Performance Skill Rank Deaths Average': {
    id: 92,
    text: 'Average',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      return (
        (team &&
          'Players' in team &&
          team.Players.map((p) =>
            'Skill' in p ? skillRank(p.Skill, 'Deaths', 'Count') : null
          ).average()) ||
        null
      );
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Player team's average performance skill rank - deaths",
  } as TableColumnWithFormat<number | null>,
  'Team Performance Skill Rank Deaths Min': {
    id: 93,
    text: 'Min',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const truthyValues =
        team && 'Players' in team
          ? team.Players.map(
              (p) => 'Skill' in p && skillRank(p.Skill, 'Deaths', 'Count')
            ).filter((v): v is number => v != null)
          : [];
      return truthyValues?.length ? Math.min(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Lowest performance skill rank - deaths among player team's members",
  } as TableColumnWithFormat<number | null>,
  'Team Performance Skill Rank Deaths Max': {
    id: 94,
    text: 'Max',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const truthyValues =
        team && 'Players' in team
          ? team.Players.map(
              (p) => 'Skill' in p && skillRank(p.Skill, 'Deaths', 'Count')
            ).filter((v): v is number => v != null)
          : [];
      return truthyValues?.length ? Math.max(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Highest performance skill rank - deaths among player team's members",
  } as TableColumnWithFormat<number | null>,
  'Enemy Expected Skill Rank Average': {
    id: 95,
    text: 'Average',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;

      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      return (
        enemyTeamPlayers
          .map((p) => skillRankCombined(p.Skill, 'Expected'))
          .average() || null
      );
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Enemy team's average expected skill rank",
  } as TableColumnWithFormat<number | null>,
  'Enemy Expected Skill Rank Min': {
    id: 96,
    text: 'Min',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      const truthyValues = enemyTeamPlayers
        .map((p) => skillRankCombined(p.Skill, 'Expected'))
        .filter((v): v is number => v != null);
      return truthyValues.length ? Math.min(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Lowest expected skill rank among enemy team's members",
  } as TableColumnWithFormat<number | null>,
  'Enemy Expected Skill Rank Max': {
    id: 97,
    text: 'Max',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      const truthyValues = enemyTeamPlayers
        .map((p) => skillRankCombined(p.Skill, 'Expected'))
        .filter((v): v is number => v != null);
      return truthyValues.length ? Math.max(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Highest expected skill rank among enemy team's members",
  } as TableColumnWithFormat<number | null>,
  'Enemy Performance Skill Rank Kills Average': {
    id: 98,
    text: 'Average',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      return (
        enemyTeamPlayers
          .map((p) => skillRank(p.Skill, 'Kills', 'Count'))
          .average() || null
      );
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Enemy team's average performance skill rank - kills",
  } as TableColumnWithFormat<number | null>,
  'Enemy Performance Skill Rank Kills Min': {
    id: 99,
    text: 'Min',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      const truthyValues = enemyTeamPlayers
        .map((p) => skillRank(p.Skill, 'Kills', 'Count'))
        .filter((v): v is number => v != null);
      return truthyValues.length ? Math.min(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Lowest performance skill rank - kills among enemy team's members",
  } as TableColumnWithFormat<number | null>,
  'Enemy Performance Skill Rank Kills Max': {
    id: 100,
    text: 'Max',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      const truthyValues = enemyTeamPlayers
        .map((p) => skillRank(p.Skill, 'Kills', 'Count'))
        .filter((v): v is number => v != null);
      return truthyValues.length ? Math.max(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Highest performance skill rank - kills among enemy team's members",
  } as TableColumnWithFormat<number | null>,
  'Enemy Performance Skill Rank Deaths Average': {
    id: 101,
    text: 'Average',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      return (
        enemyTeamPlayers
          .map((p) => skillRank(p.Skill, 'Deaths', 'Count'))
          .average() || null
      );
    },
    format: (value) => value?.toFixed(2),
    tooltip: "Enemy team's average performance skill rank - deaths",
  } as TableColumnWithFormat<number | null>,
  'Enemy Performance Skill Rank Deaths Min': {
    id: 102,
    text: 'Min',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      const truthyValues = enemyTeamPlayers
        .map((p) => skillRank(p.Skill, 'Deaths', 'Count'))
        .filter((v): v is number => v != null);
      return truthyValues.length ? Math.min(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Lowest performance skill rank - deaths among enemy team's members",
  } as TableColumnWithFormat<number | null>,
  'Enemy Performance Skill Rank Deaths Max': {
    id: 103,
    text: 'Max',
    level: 'Team',
    cellProps: { isNumeric: true },
    rawValue: ({ match, team }) => {
      if (match.MatchInfo.TeamsEnabled === false) return null;
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter((p) => p.LastTeamId != team?.TeamId)
          : [];
      const truthyValues = enemyTeamPlayers
        .map((p) => skillRank(p.Skill, 'Deaths', 'Count'))
        .filter((v): v is number => v != null);
      return truthyValues.length ? Math.max(...truthyValues) : null;
    },
    format: (value) => value?.toFixed(2),
    tooltip:
      "Highest performance skill rank - deaths among enemy team's members",
  } as TableColumnWithFormat<number | null>,
  Gamertag: {
    id: 104,
    text: 'Gamertag',
    level: 'Player',
    rawValue: ({ player }) =>
      ('gamertag' in player && player.gamertag) ||
      ('xuid' in player && player.xuid) ||
      player.PlayerId,
    format: (_, { player }) =>
      ('gamertag' in player || 'PlayerId' in player) && (
        <GamertagDisplay player={player} fetchIfMissing />
      ),
  },
  ...playerStatsColumns.columns,
  'Pre CSR': {
    id: 174,
    text: 'Pre',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ match, player }) => {
      if (
        match.MatchInfo.Playlist &&
        'PublicName' in match.MatchInfo.Playlist &&
        'HasCsr' in match.MatchInfo.Playlist &&
        match.MatchInfo.Playlist.HasCsr === false
      ) {
        return null;
      }
      return 'Skill' in player && player.Skill?.RankRecap.PreMatchCsr.Value;
    },
    tooltip: "Player's competitive skill ranking before the match",
    format: (value) => (value != null && value > -1 ? value : null),
  } as TableColumnWithFormat<number | null | undefined>,
  'Post CSR': {
    id: 175,
    text: 'Post',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ match, player }) => {
      if (
        match.MatchInfo.Playlist &&
        'PublicName' in match.MatchInfo.Playlist &&
        'HasCsr' in match.MatchInfo.Playlist &&
        match.MatchInfo.Playlist.HasCsr === false
      ) {
        return null;
      }
      return 'Skill' in player && player.Skill?.RankRecap.PostMatchCsr.Value;
    },
    tooltip: "Player's competitive skill ranking after the match",
    format: (value) => (value != null && value > -1 ? value : null),
  } as TableColumnWithFormat<number | null | undefined>,
  'CSR Delta': {
    id: 176,
    text: 'Delta',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) => {
      if (
        !('Skill' in player) ||
        player.Skill?.RankRecap == null ||
        player.Skill.RankRecap.PreMatchCsr.Value < 0 ||
        player.Skill.RankRecap.PostMatchCsr.Value < 0
      ) {
        return null;
      }

      return (
        player.Skill.RankRecap.PostMatchCsr.Value -
        player.Skill.RankRecap.PreMatchCsr.Value
      );
    },
    format: (_, { match, team, player }) => {
      if (
        match.MatchInfo.Playlist &&
        'PublicName' in match.MatchInfo.Playlist &&
        'HasCsr' in match.MatchInfo.Playlist &&
        match.MatchInfo.Playlist.HasCsr === false
      ) {
        return null;
      }
      const enemyTeamPlayers =
        'MatchStats' in match
          ? match.MatchStats.Players.filter(
              (p) => p.LastTeamId !== team?.TeamId
            )
          : [];
      return (
        <CsrDeltaDisplay
          skill={'Skill' in player ? player.Skill : undefined}
          outcome={player.Outcome}
          allEnemiesFinished={enemyTeamPlayers.every(
            (p) => p.ParticipationInfo.PresentAtCompletion
          )}
          allTeammatesFinished={
            (team &&
              'Players' in team &&
              team.Players.every(
                (p) => p.ParticipationInfo.PresentAtCompletion
              )) ??
            true
          }
          teamSkills={
            team && 'Players' in team
              ? team.Players.map((p) => ('Skill' in p ? p.Skill : undefined))
              : []
          }
        />
      );
    },
    tooltip: 'Change in player competitive skill ranking',
  },
  ...counterfactualColumns(
    'Player',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.SelfCounterfactuals
        : undefined,
    177
  ),
  ...counterfactualColumns(
    'Bronze',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.TierCounterfactuals.Bronze
        : undefined,
    181
  ),
  ...counterfactualColumns(
    'Silver',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.TierCounterfactuals.Silver
        : undefined,
    185
  ),
  ...counterfactualColumns(
    'Gold',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.TierCounterfactuals.Gold
        : undefined,
    189
  ),
  ...counterfactualColumns(
    'Platinum',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.TierCounterfactuals.Platinum
        : undefined,
    193
  ),
  ...counterfactualColumns(
    'Diamond',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.TierCounterfactuals.Diamond
        : undefined,
    197
  ),
  ...counterfactualColumns(
    'Onyx',
    ({ player }) =>
      'Skill' in player
        ? player.Skill?.Counterfactuals?.TierCounterfactuals.Onyx
        : undefined,
    201
  ),
  'Player Expected Skill Rank Kills': {
    id: 205,
    text: 'Kills',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) =>
      (player &&
        'Skill' in player &&
        skillRank(player.Skill, 'Kills', 'Expected')) ||
      null,
    format: (value) => value?.toFixed(2),
    tooltip: "Player's expected skill rank - kills",
  } as TableColumnWithFormat<number | null>,
  'Player Expected Skill Rank Deaths': {
    id: 206,
    text: 'Deaths',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) =>
      (player &&
        'Skill' in player &&
        skillRank(player.Skill, 'Kills', 'Expected')) ||
      null,
    format: (value) => value?.toFixed(2),
    tooltip: "Player's expected skill rank - deaths",
  } as TableColumnWithFormat<number | null>,
  'Player Expected Skill Rank Combined': {
    id: 207,
    text: 'Combined',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) =>
      (player &&
        'Skill' in player &&
        skillRankCombined(player.Skill, 'Expected')) ||
      null,
    format: (esr) => esr?.toFixed(2),
    tooltip: "Player's expected skill rank - kills/deaths average",
  } as TableColumnWithFormat<number | null>,
  'Player Performance Skill Rank Kills': {
    id: 208,
    text: 'Kills',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) =>
      'Skill' in player && player.Skill
        ? skillRank(player.Skill, 'Kills', 'Count')
        : null,
    format: (value) => value?.toFixed(2),
    tooltip: "Player's performance skill rank - kills",
  } as TableColumnWithFormat<number | null>,
  'Player Performance Skill Rank Deaths': {
    id: 209,
    text: 'Deaths',
    level: 'Player',
    cellProps: { isNumeric: true },
    rawValue: ({ player }) =>
      'Skill' in player && player.Skill
        ? skillRank(player.Skill, 'Deaths', 'Count')
        : null,
    format: (value) => value?.toFixed(2),
    tooltip: "Player's performance skill rank - deaths",
  } as TableColumnWithFormat<number | null>,
} satisfies Record<string, TableColumn>;

// throw if any column's id is not unique
Object.values(columns).forEach((column) => {
  if (Object.values(columns).some((c) => c.id === column.id && c !== column)) {
    throw new Error(
      `Duplicate column id found: ${column.id}. Next available ID is ${
        Math.max(...Object.values(columns).map((c) => c.id)) + 1
      }.`
    );
  }
});

export type ColumnName = keyof typeof columns;

export const categories: TableCategory<TableCategory>[] = [
  {
    children: [
      {
        children: [
          columns['Match Id'],
          columns['Start Time'],
          columns['End Time'],
          columns['Playlist'],
          columns['Map'],
          columns['Game Variant Category'],
          columns['Game Variant'],
          columns['Duration'],
          columns['Outcome'],
          columns['Season'],
          columns['Team Members'],
          columns['Enemy Team Members'],
        ],
      },
      {
        text: 'Scores',
        children: [columns['Player Team Score'], columns['Enemy Team Score']],
      },
    ],
  },
  {
    text: 'Team Stats',
    children: teamStatsColumns.categories,
  },
  {
    children: [
      {
        text: 'Team CSR',
        children: [columns['Player Team CSR'], columns['Enemy Team CSR']],
      },
    ],
  },
  {
    children: [
      {
        text: 'Team MMR',
        children: [columns['Player Team MMR'], columns['Enemy Team MMR']],
      },
    ],
  },
  {
    text: 'Team Skill Rank',
    children: [
      {
        text: 'Expected',
        children: [
          columns['Team Expected Skill Rank Average'],
          columns['Team Expected Skill Rank Min'],
          columns['Team Expected Skill Rank Max'],
        ],
      },
      {
        text: 'Performance - Kills',
        children: [
          columns['Team Performance Skill Rank Kills Average'],
          columns['Team Performance Skill Rank Kills Min'],
          columns['Team Performance Skill Rank Kills Max'],
        ],
      },
      {
        text: 'Performance - Deaths',
        children: [
          columns['Team Performance Skill Rank Deaths Average'],
          columns['Team Performance Skill Rank Deaths Min'],
          columns['Team Performance Skill Rank Deaths Max'],
        ],
      },
    ],
  },
  {
    text: 'Enemy Skill Rank',
    children: [
      {
        text: 'Expected',
        children: [
          columns['Enemy Expected Skill Rank Average'],
          columns['Enemy Expected Skill Rank Min'],
          columns['Enemy Expected Skill Rank Max'],
        ],
      },
      {
        text: 'Performance - Kills',
        children: [
          columns['Enemy Performance Skill Rank Kills Average'],
          columns['Enemy Performance Skill Rank Kills Min'],
          columns['Enemy Performance Skill Rank Kills Max'],
        ],
      },
      {
        text: 'Performance - Deaths',
        children: [
          columns['Enemy Performance Skill Rank Deaths Average'],
          columns['Enemy Performance Skill Rank Deaths Min'],
          columns['Enemy Performance Skill Rank Deaths Max'],
        ],
      },
    ],
  },
  {
    children: [
      {
        children: [columns['Gamertag']],
      },
    ],
  },
  playerStatsCategory,
  {
    children: [
      {
        text: 'CSR',
        children: [
          columns['Pre CSR'],
          columns['Post CSR'],
          columns['CSR Delta'],
        ],
      },
    ],
  },
  {
    text: 'Counterfactuals',
    children: [
      {
        text: 'Player',
        children: [
          columns['Player Expected Kills'],
          columns['Player Expected Kills per Minute'],
          columns['Player Expected Deaths'],
          columns['Player Expected Deaths per Minute'],
        ],
      },
      ...(
        ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Onyx'] as const
      ).map((tier) => ({
        text: tier,
        children: [
          columns[`${tier} Expected Kills` as keyof typeof columns],
          columns[`${tier} Expected Kills per Minute` as keyof typeof columns],
          columns[`${tier} Expected Deaths` as keyof typeof columns],
          columns[`${tier} Expected Deaths per Minute` as keyof typeof columns],
        ],
      })),
    ],
  },
  {
    text: 'Player Skill Rank',
    children: [
      {
        text: 'Expected',
        children: [
          columns['Player Expected Skill Rank Kills'],
          columns['Player Expected Skill Rank Deaths'],
          columns['Player Expected Skill Rank Combined'],
        ],
      },
      {
        text: 'Performance',
        children: [
          columns['Player Performance Skill Rank Kills'],
          columns['Player Performance Skill Rank Deaths'],
        ],
      },
    ],
  },
];

export const orderedColumns = categories.flatMap((topLevelCategory) =>
  topLevelCategory.children.flatMap(
    (secondLevelCategory) => secondLevelCategory.children
  )
);
